import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { apiGetDevices } from '@/features/devices/api/api-get-devices'
import { DeviceFiltersPanel } from '@/features/devices/components/device-filters-panel'
import { formatDateAdded } from '@/features/devices/lib/format-date-added'
import {
  emptyDeviceFilters,
  type DeviceFilters,
} from '@/features/devices/types/device-filters'
import type { Device } from '@/features/devices/types/device'
import type { Location } from '@/shared/types/location'
import { emptyPagination } from '@/lib/api/empty-pagination'
import {
  Button,
  ListTable,
  LoadingSpinner,
  PaginationLinks,
  StatusBadge,
  type ListTableColumn,
} from '@/shared/components'
import { useApiResource } from '@/shared/hooks/use-api-resource'
import FilterIcon from '@/assets/icons/filter.svg'

const BLANK_CELL = '---'

const LocationCell = ({ location }: { location: Location | null }) => {
  if (!location) {
    return BLANK_CELL
  }

  const locationDetails = [location.town, location.asset_location].filter(Boolean)

  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-bold">{location.location_id}</span>
      {locationDetails.length > 0 ? (
        <span className="text-sm">{locationDetails.join(', ')}</span>
      ) : null}
    </div>
  )
}

export const DevicesPage = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<DeviceFilters>(emptyDeviceFilters())
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)

  const loadDevices = useCallback(
    () => apiGetDevices(filters, page),
    [filters, page]
  )
  const { data, error, isLoading } = useApiResource(
    loadDevices,
    'Failed to load devices'
  )
  const devices = data?.data ?? []
  const pagination = data?.pagination ?? emptyPagination()

  const handleApplyFilters = (nextFilters: DeviceFilters) => {
    setPage(1)
    setFilters(nextFilters)
  }

  const columns = useMemo<ListTableColumn<Device>[]>(
    () => [
      {
        id: 'date-added',
        header: 'Date Added',
        width: 'auto',
        cell: (device) =>
          device.date_added ? formatDateAdded(device.date_added) : BLANK_CELL,
      },
      {
        id: 'device',
        header: 'Device',
        width: 'fill',
        cell: (device) => device.serial_number,
      },
      {
        id: 'current-location-id',
        header: 'Current location ID',
        width: 'fill',
        cell: (device) => <LocationCell location={device.location} />,
      },
      {
        id: 'status',
        header: 'Status',
        width: 'auto',
        cell: (device) => <StatusBadge status={device.status} />,
      },
    ],
    []
  )

  const tableContent = (() => {
    if (isLoading) {
      return <LoadingSpinner position="center" label="Loading devices" />
    }

    if (error) {
      return (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )
    }

    return (
      <>
        <ListTable
          columns={columns}
          data={devices}
          getRowKey={(device) => device.id}
          emptyMessage="No devices found."
          onRowClick={(device) => navigate(`/devices/${device.id}`)}
        />

        <PaginationLinks
          pagination={pagination}
          onPageChange={setPage}
          className="pt-4"
        />
      </>
    )
  })()

  return (
    <>
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          icon={FilterIcon}
          variant="secondary-light"
          className="self-start"
          onClick={() => setIsFiltersOpen(true)}
        >
          Filter
        </Button>

        {tableContent}
      </div>

      <DeviceFiltersPanel
        open={isFiltersOpen}
        onOpenChange={setIsFiltersOpen}
        filters={filters}
        onApply={handleApplyFilters}
      />
    </>
  )
}
